import { useEffect, useMemo, useState } from "react";
import { Subject } from "rxjs";
import { IScheduler } from "rxjs/Scheduler";
import { useImmutableCallback } from "./useImmutableCallback";

const DEFAULT_ON_CHANGE_DELAY = 300;
const defaultGetEventValue = <E = any>(e: E) => e;

export type Params<V = any, E = any> = {
    value?: V;
    getEventValue?: (e: E) => V;
    onEvent: (value: V) => void;
    onEventDelay?: number;
    scheduler?: IScheduler;
};

/**
 * Provides local state management for controlled components, and ensures consistent
 * value state when the local state is updated at once, but the `onChange` callback
 * is called with debounce delay for propagating to the global state.
 *
 * @param value The value from the global state, which initializes the local state.
 * @param getEventValue Resolves the value from the event that is sunk to `handleChange`.
 * @param onEvent Callback that is called in debounce manner.
 * @param onEventDelay Optional, the `onChange` debounce delay, in ms.
 * @param scheduler
 *
 * @returns {[V, (e: E) => void]} A two-item array with a currently displayed value and the event handler.
 */
export function useDebouncedHandler<E = any, V = any>({
    value,
    getEventValue = defaultGetEventValue as (e: E) => V,
    onEvent,
    onEventDelay = DEFAULT_ON_CHANGE_DELAY,
    scheduler,
}: Params<V, E>): readonly [V, (e: E) => void] {
    const [displayValue, setDisplayValue] = useState(value);
    const getEventValueCb = useImmutableCallback(getEventValue);
    const onEventCb = useImmutableCallback(onEvent);

    const { handleEvent, updateValue, allValues$ } = useMemo(() => {
        const events$ = new Subject<E>();
        const handleEvent = events$.next.bind(events$);
        const values$ = new Subject<V>();
        const updateValue = values$.next.bind(values$);

        const allValues$ = values$
            .merge(events$.map(getEventValueCb))
            .distinctUntilChanged()
            .pairwise()
            .map(([, val]) => val)
            .share();

        return {
            allValues$,
            handleEvent,
            updateValue,
        };
    }, [getEventValueCb]);

    useEffect(() => {
        const subs = [
            // prettier, no please no
            allValues$.debounceTime(onEventDelay, scheduler).subscribe(onEventCb),
            allValues$.subscribe(setDisplayValue),
        ];

        return () => subs.forEach((sub) => sub.unsubscribe());

        // in reality, these deps won't be changed, so no re-subscription or replay
    }, [allValues$, onEventCb, onEventDelay, scheduler]);

    useEffect(() => {
        updateValue(value);
    }, [updateValue, value]);

    return [displayValue, handleEvent] as const;
}
